/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useQuery, ApolloError } from '@apollo/client'
import {
  ALL_AGENCIES,
  ALL_PROGRAMS,
  ALL_PROGRAM_FILTER_CATEGORY_MAPS,
  ALL_PROGRAM_FILTER_MAPS,
  ALL_REGIONS,
} from '../queries'
import { useTranslation } from 'react-i18next'
import uniqBy from 'lodash/uniqBy'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import {
  IAgency,
  IFilterItem,
  IProgram,
  IProgramFilterCategoryMap,
  IProgramFilterMap,
  IRegion,
} from '../interfaces'
import { FilterInputType, Ages, SortOptions } from '../constants/enums'
import { mergeByKey } from '../utils'
import find from 'lodash/find'
export interface IUseGetPrograms {
  filterCategoryId: number
  filters: IFilterItem[]
  sort: SortOptions
}

export interface IUseGetProgramsResult {
  loading: boolean
  error?: ApolloError
  programs?: IProgram[]
  agencies?: IAgency[]
  regions?: IRegion[]
  sort?: SortOptions
}

const onSortPrograms = function (sort: SortOptions | undefined, programs: IProgram[]) {
  if (sort === SortOptions.AZ) {
    return programs
      .filter((p: IProgram) => p.agency !== undefined)
      .sort((a: IProgram, b: IProgram) => {
        // @ts-ignore
        if (a.agency.name < b.agency.name) {
          return -1
        }
        // @ts-ignore
        if (a.agency.name > b.agency.name) {
          return 1
        }
        return 0
      })
  }

  if (sort === SortOptions.NEIGHBORHOOD) {
    return programs
      .filter((p: IProgram) => p.region !== undefined)
      .sort((a: IProgram, b: IProgram) => {
        // @ts-ignore
        if (a.region.description < b.region.description) {
          return -1
        }
        // @ts-ignore
        if (a.region.description > b?.region.description) {
          return 1
        }
        return 0
      })
  }

  return programs
}

const getProgramValues = function ({
  loading,
  error,
  programs,
  agencies,
  regions,
  sort,
}: IUseGetProgramsResult) {
  if (programs && agencies) {
    return {
      loading,
      error,
      programs: onSortPrograms(
        sort,
        programs.map((program: IProgram) => {
          return {
            ...program,
            agency: agencies.find((agency) => agency.id === program?.agencyId),
            region: find(regions, { id: program?.areaRegionId }),
          }
        }),
      ),
    }
  }
  return { loading, error, programs }
}

const useGetPrograms = function ({
  filterCategoryId,
  filters,
  sort,
}: IUseGetPrograms): IUseGetProgramsResult {
  const { i18n } = useTranslation()
  const LANG = i18n.language.toLocaleUpperCase() as 'EN' | 'ES'

  const { loading, error, data } = useQuery(ALL_PROGRAMS[LANG])

  const {
    loading: loadingProgramFilterCategoryMaps,
    error: errorProgramFilterCategoryMaps,
    data: programFilterCategoryMapsData,
  } = useQuery(ALL_PROGRAM_FILTER_CATEGORY_MAPS, { fetchPolicy: 'no-cache' })

  const {
    loading: loadingProgramFilterMaps,
    error: errorProgramFilterMaps,
    data: programFilterMapsData,
  } = useQuery(ALL_PROGRAM_FILTER_MAPS, { fetchPolicy: 'no-cache' })

  const {
    loading: loadingAgencies,
    error: errorAgencies,
    data: agenciesData,
  } = useQuery(ALL_AGENCIES)

  const { loading: loadingRegions, error: errorRegions, data: regionsData } = useQuery(ALL_REGIONS)

  if (loading) return getProgramValues({ loading, error })

  if (loadingProgramFilterCategoryMaps) {
    return getProgramValues({
      loading: loadingProgramFilterCategoryMaps,
      error: errorProgramFilterCategoryMaps,
    })
  }

  if (loadingProgramFilterMaps) {
    return getProgramValues({ loading: loadingProgramFilterMaps, error: errorProgramFilterMaps })
  }

  if (loadingAgencies) {
    return getProgramValues({ loading: loadingAgencies, error: errorAgencies })
  }

  if (loadingRegions) {
    return getProgramValues({ loading: loadingRegions, error: errorRegions })
  }

  const allRegions = regionsData['regions_ALL']['data']

  const regions = allRegions.filter(
    (region: IRegion) =>
      region.isWildcard === false && region.areaId === 1 && region.description !== 'All',
  )

  const allAgencies = agenciesData['agencies_LA']['data']

  const p1 = get(data[`programs_${LANG}_LAS`], '[0].data')
  const p2 = get(data[`programs_${LANG}_LAS`], '[1].data')
  const p3 = get(data[`programs_${LANG}_LAS`], '[2].data')
  const p4 = get(data[`programs_${LANG}_LAS`], '[3].data')
  const p5 = get(data[`programs_${LANG}_LAS`], '[4].data')

  const allPrograms = p1.concat(p2).concat(p3).concat(p4).concat(p5)

  const allProgramFilterCategoryMaps: IProgramFilterCategoryMap[] =
    programFilterCategoryMapsData['programFilterCategoriesMaps_LA']['data']

  const allProgramFilterMaps: IProgramFilterMap[] =
    programFilterMapsData['programFilterMaps_LA']['data']

  const selectedFilterCategoryMaps = allProgramFilterCategoryMaps.filter(
    (map: IProgramFilterCategoryMap) => map.filterCategoryId === filterCategoryId,
  )

  // Category filter
  const selectedProgramsFromCategory = selectedFilterCategoryMaps
    .map((map: IProgramFilterCategoryMap) => {
      return allPrograms.find(
        (program: IProgram) => program.isActive && program.id === map.programId,
      )
    })
    .filter((program: IProgram | undefined) => program !== undefined)

  if (!isEmpty(filters)) {
    // Checkbox filters
    let checkboxFilterMaps: IProgramFilterMap[] = []
    const checkboxFilters = filters.filter((filter) => filter.type === FilterInputType.CHECKBOX)
    if (!isEmpty(checkboxFilters)) {
      checkboxFilterMaps = allProgramFilterMaps.filter((map: IProgramFilterMap) =>
        checkboxFilters.map((filter) => filter.id).includes(map.filterId),
      )
    }

    // Dynamic multi select filters
    let multiselectFilterMaps: IProgramFilterMap[] = []
    const multiSelectFilters = filters.filter(
      (filter) => filter.type === FilterInputType.DYNAMIC_MULTI_SELECT,
    )
    if (!isEmpty(multiSelectFilters)) {
      multiselectFilterMaps = allProgramFilterMaps.filter((map: IProgramFilterMap) =>
        multiSelectFilters.map((filter) => filter.id).includes(map.filterId),
      )
    }

    // Checkbox filters
    const selectedProgramsFromCheckboxFilters = checkboxFilterMaps.map((map: IProgramFilterMap) => {
      return allPrograms.find((program: IProgram) => program && program.id === map.programId)
    })

    // Multiselect can depend on checkbox filtered data
    const multiselectAvailablePrograms = isEmpty(selectedProgramsFromCheckboxFilters)
      ? selectedProgramsFromCategory
      : selectedProgramsFromCheckboxFilters

    // Multiselect filters ( Community, Gender )
    const selectedProgramsFromMultiselectFilters = multiselectFilterMaps.map(
      (map: IProgramFilterMap) => {
        return multiselectAvailablePrograms.find(
          (program: IProgram) => program && program.id === map.programId,
        )
      },
    )

    // Ages filters
    const agesFilters = filters.filter((filter) => filter.type === FilterInputType.AGES)
    let selectedProgramsFromAgesFilters = []
    if (!isEmpty(agesFilters)) {
      selectedProgramsFromAgesFilters = multiselectAvailablePrograms.filter((program: IProgram) => {
        if (!program) return undefined
        return agesFilters.some((filter) => {
          switch (filter.id) {
            case Ages.UNDER_18:
              return program.ageFrom < 18
            case Ages.YOUTH_ONLY:
              return program.ageTo <= 25
            case Ages.ADULT_ONLY:
              return program.ageFrom >= 18 && program.ageTo >= 55
            default:
              return false
          }
        })
      })
    }

    // Neighborhood filters
    const neighborhoodFilters = filters.filter(
      (filter) => filter.type === FilterInputType.NEIGHBORHOOD,
    )
    let selectedProgramsFromNeighborhoodFilters = []
    if (!isEmpty(neighborhoodFilters)) {
      selectedProgramsFromNeighborhoodFilters = multiselectAvailablePrograms.filter(
        (program: IProgram) => {
          return neighborhoodFilters.map((filter) => filter.id).includes(program?.areaRegionId)
        },
      )
    }

    const allFilteredPrograms = uniqBy(
      (isEmpty(selectedProgramsFromMultiselectFilters) &&
      isEmpty(selectedProgramsFromAgesFilters) &&
      isEmpty(selectedProgramsFromNeighborhoodFilters)
        ? selectedProgramsFromCheckboxFilters
        : mergeByKey(
            mergeByKey(
              selectedProgramsFromMultiselectFilters,
              selectedProgramsFromAgesFilters,
              'id',
            ),
            selectedProgramsFromNeighborhoodFilters,
            'id',
          )
      ).filter((program: IProgram | undefined) => program !== undefined),
      'id',
    )

    return getProgramValues({
      loading,
      error,
      programs: allFilteredPrograms,
      agencies: allAgencies,
      sort,
      regions,
    })
  }

  return getProgramValues({
    loading,
    error,
    programs: uniqBy(selectedProgramsFromCategory, 'id'),
    agencies: allAgencies,
    sort,
    regions,
  })
}

export default useGetPrograms
